import React from 'react';
import { Link } from 'gatsby';
import { Tag } from '../Tag';
import truncate from '../../utils/truncate';
import getHelpFileURL from '../../utils/getHelpFileURL';
import Button from '../Button';
import isExternalURL from '../../utils/isExternalURL';
import { makeAuthorPath } from '../../paths';

const format = require('date-fns/format');

function findBySlugOrUseFirst(items, slug) {
  if (!items) return null;
  if (!slug) return items[0];

  const item = items.find((item) => item.slug === slug);

  return item || items[0];
}

export function BlogPostCardBGImage({ node }) {
  return (
    <div
      className="w-full bg-cover bg-no-repeat aspect-video rounded-[13px] mb-0.5"
      style={{
        backgroundColor: '#eee',
        backgroundPosition: 'center',
        backgroundImage: node.bg_image,
      }}
    />
  );
}

export function BlogPostCardTitle({ node }) {
  return (
    <div className="text-black font-sonoma font-light !leading-[1.3] mb-1 !text-lg">
      {node.title}
    </div>
  );
}

export function BlogCardContent({
  node,
  authorSlug,
  tagSlug,
  isBlock,
  fullDescription = false,
  linkTarget = '',
}) {
  const author =
    node.authors && authorSlug
      ? findBySlugOrUseFirst(node.authors, authorSlug)
      : null;
  const tag =
    node.tags && tagSlug ? findBySlugOrUseFirst(node.tags, tagSlug) : null;

  return (
    <div className="flex flex-1 flex-col justify-start">
      {(tag || node.tag) && (
        <div className="mb-2">
          <Tag label={tag ? tag.name : node.tag} slug={tag && tag.slug} />
        </div>
      )}

      {node.slug || node.href ? (
        <Link
          to={
            node.href
              ? node.href
              : node.slug.includes('/')
              ? node.slug
              : `/blog/${node.slug}/`
          }
          href={node.href}
          className="color-inherit decoration-none"
          target={linkTarget}
        >
          <BlogPostCardTitle node={node} />
        </Link>
      ) : (
        <BlogPostCardTitle node={node} />
      )}

      <p className={`${isBlock ? 'text-sm' : 'text-xs'} text-black/50 my-1`}>
        {fullDescription
          ? node.excerpt || node.custom_excerpt
          : truncate(node.excerpt || node.custom_excerpt || '', 100)}
      </p>

      {author && (
        <div className="flex justify-between items-center pt-2 mt-auto">
          <div className="flex items-center">
            <div
              style={{
                backgroundImage: `url(https://img.paperform.co/fetch/w_120,h_120/${author.profile_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              className="w-7 h-7 rounded-full m-0"
            />
            <Link
              to={makeAuthorPath(author.slug, 1)}
              className="text-[13px] ml-1.5 text-black/[.5]"
            >
              {author.name}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function BlogPostCard({
  node: nodeProp,
  authorSlug,
  tagSlug,
  isNetlify = false,
  isBlock = false,
  fullDescription,
}) {
  const node = JSON.parse(JSON.stringify(nodeProp));

  if (!authorSlug) {
    authorSlug =
      node.authors && node.authors.length ? node.authors[0].slug : '';
  }
  if (!tagSlug) {
    tagSlug = node.tags && node.tags.length ? node.tags[0].slug : '';
  }
  let linkTarget = '';
  if (isBlock) {
    node.slug = '';
    node.href = node?.link?.link_url || '';
    node.bg_image = node.feature_image
      ? `url(${getHelpFileURL(node.feature_image)})`
      : 'none';
    if (isExternalURL(node.href)) linkTarget = '_blank';
  } else if (isNetlify) {
    node.slug = '';
    node.href = node.slug || '';
    node.bg_image = node.feature_image
      ? `url(${getHelpFileURL(node.feature_image)})`
      : 'none';
    if (isExternalURL(node.href)) linkTarget = '_blank';
  } else {
    if (!node?.slug.includes('/blog/')) node.slug = `/blog/${node.slug}/`;
    node.href = '';
    node.bg_image = node.feature_image
      ? `url(https://img.paperform.co/fetch/w_900,f_auto/${node.feature_image})`
      : 'none';
  }

  return (
    <div className="flex flex-col p-2 w-80 bg-white rounded-[23px] overflow-hidden">
      {node.slug || node.href ? (
        <Link to={node.slug} href={node.href} target={linkTarget}>
          <BlogPostCardBGImage node={node} />
        </Link>
      ) : (
        <BlogPostCardBGImage node={node} />
      )}

      <div className="flex flex-1 flex-col justify-between items-start py-3 px-4 gap-4">
        <BlogCardContent
          node={node}
          authorSlug={authorSlug}
          tagSlug={tagSlug}
          fullDescription={fullDescription}
          isBlock={isBlock}
          linkTarget={linkTarget}
        />
        {isBlock && node?.link?.link_text ? (
          <Button
            href={node.href}
            solid
            color={node.href ? 'yellow' : 'grey'}
            disabled={node.href ? false : 'disabled'}
          >
            {node.link.link_text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
